<template>
  <form @submit="handleSubmit" action="/" method="post">
    <base-section>
      <intro-img />
      <h1 v-t="'wizard.intro.title'" />
      <button-wrapper>
        <base-button @click.native="handleStart" v-t="'wizard.intro.start'" />
      </button-wrapper>
    </base-section>

    <base-section ref="business-category">
      <h2><label for="businessCategory" v-t="'wizard.category.title'" /></h2>
      <base-select
        :required="checkRequired('businessCategory')"
        v-model="businessCategory"
        @input="handleSelectCategory"
        name="businessCategory"
        id="businessCategory"
      >
        <option value selected disabled v-t="'wizard.category.choose'" />
        <option
          v-for="category in $t('wizard.category.categories')"
          :key="category"
          :value="category"
          v-text="category"
        ></option>
      </base-select>
    </base-section>

    <base-section ref="company">
      <h2 v-t="'wizard.company.title'" />
      <radio-group
        name="clientType"
        v-model="clientType"
        @input="handleClientTypeChange"
        :required="checkRequired('clientType')"
        :values="{
          company: $t('wizard.company.company'),
          solo: $t('wizard.company.solo'),
        }"
      />
    </base-section>

    <base-section ref="details" v-if="clientType">
      <h2 v-t="`wizard.details.${clientType}.title`" />

      <input-group
        :required="checkRequired('name')"
        :title="$t(`wizard.details.${clientType}.name`)"
        v-model="name"
        name="name"
      />

      <input-group
        :required="checkRequired('contactNumber')"
        :title="$t(`wizard.details.${clientType}.contactNumber`)"
        :placeholder="$t(`wizard.details.${clientType}.contactNumberPlaceholder`)"
        v-model="contactNumber"
        name="contactNumber"
        type="tel"
      />

      <input-group
        v-if="clientType === 'company'"
        :required="checkRequired('physicalAddress')"
        :title="$t('wizard.details.company.physicalAddress')"
        :placeholder="$t('wizard.details.company.physicalAddressPlaceholder')"
        v-model="physicalAddress"
        name="physicalAddress"
        :rows="2"
      />

      <input-group
        :required="checkRequired('servicingArea')"
        :title="$t(`wizard.details.${clientType}.servicingArea`)"
        :placeholder="$t(`wizard.details.${clientType}.servicingAreaPlaceholder`)"
        v-model="servicingArea"
        name="servicingArea"
        :rows="servicingAreaRows"
      />

      <map-preview :address="servicingArea" />

      <input-group :title="$t(`wizard.details.${clientType}.schedule`)">
        <input-time v-model="schedule" />
      </input-group>

      <button-wrapper>
        <base-button @click.native="handleCompleteDetails" small v-t="'wizard.details.next'" />
      </button-wrapper>
    </base-section>

    <base-section ref="user-avatar">
      <hgroup>
        <h2><label for="user-avatar" v-t="'wizard.userAvatar.title'" /></h2>
        <h4 v-t="'wizard.userAvatar.description'" />
      </hgroup>
      <input-file id="user-avatar" v-model="userAvatar" name="userAvatar" @load="setUserAvatar" />

      <button-wrapper>
        <base-button @click.native="handleCompleteAvatar" small v-t="'wizard.userAvatar.next'" />
      </button-wrapper>
    </base-section>

    <base-section ref="submit">
      <h2><label for="email" v-t="'wizard.submit.email'" /></h2>
      <base-input
        :required="checkRequired('email')"
        :placeholder="$t('wizard.submit.emailPlaceholder')"
        v-model="email"
        id="email"
        type="email"
      />

      <button-wrapper>
        <base-button :disabled="disabled" type="submit" v-t="'wizard.submit.button'" />
        <wizard-terms />
      </button-wrapper>
    </base-section>
    <wizard-footer />
  </form>
</template>

<script>
import ButtonWrapper from '@/components/ButtonWrapper';
import RadioGroup from '@/components/RadioGroup/RadioGroup';
import InputGroup from '@/components/InputGroup';
import IntroImg from '@/components/IntroImg/IntroImg';
import WizardFooter from '@/components/WizardFooter/WizardFooter';
import WizardTerms from '@/components/WizardTerms/WizardTerms';
import InputFile from '@/components/InputTypeFile/InputTypeFile';
import InputTime from '@/components/InputTime/InputTime';
import MapPreview from '@/components/MapPreview/MapPreview';
import store from '@/store';

const REQUIRED_FIELDS = ['clientType', 'businessCategory', 'email'];

export default {
  components: {
    ButtonWrapper,
    InputGroup,
    RadioGroup,
    IntroImg,
    WizardFooter,
    WizardTerms,
    InputFile,
    InputTime,
    MapPreview,
  },
  data() {
    return store.wizard;
  },
  computed: {
    disabled() {
      return REQUIRED_FIELDS.some(field => !this[field]);
    },
    servicingAreaRows() {
      const { matches } = window.matchMedia('(min-width: 321px)');
      return matches ? 3 : 4;
    },
  },
  methods: {
    checkRequired(name) {
      return REQUIRED_FIELDS.includes(name);
    },
    scrollTo(refName) {
      setTimeout(() => {
        const ref = this.$refs[refName];
        const element = ref ? ref.$el || ref : null;
        if (!element) return;

        element.querySelector('select, input').focus();
        element.scrollIntoView();
      }, 50);
    },
    setUserAvatar(file) {
      this.userAvatar = file;
    },
    handleStart() {
      this.rnAppMetricaReportEvent('start', store.appMetricaData);
      this.sendGAEvent('start');
      this.scrollTo('business-category');
    },
    handleSelectCategory() {
      this.sendGAEvent('select category', { event_label: this.businessCategory });
      this.scrollTo('company');
    },
    handleClientTypeChange() {
      this.sendGAEvent('change client type', { event_label: this.clientType });
      this.scrollTo('details');
    },
    handleCompleteDetails() {
      this.sendGAEvent('complete details');
      this.scrollTo('user-avatar');
    },
    handleCompleteAvatar() {
      this.sendGAEvent('complete avatar', { event_label: !!this.userAvatar });
      this.scrollTo('submit');
    },
    handleSubmit(event) {
      event.preventDefault();
      this.rnAppMetricaReportEvent('create_site', store.appMetricaData);
      this.sendGAEvent('submit');
      this.$hideError();
      store.wizard.metadata.referrer = window.location.href;
      this.$router.push({
        name: 'creating',
        params: { data: this.$data },
      });
    },
  },
};
</script>
